import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Rating,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  FormControlLabel,
  Switch,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  Star,
  ThumbUp,
  CheckCircle,
  Cancel,
  AttachMoney,
  PersonAdd
} from '@mui/icons-material';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.spacing(2)
  },
}));

const FeedbackSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  border: '1px solid #e0e0e0',
  backgroundColor: '#fafafa',
}));

const SessionFeedbackModal = ({ 
  open, 
  onClose, 
  onSubmit, 
  sessionData,
  loading = false 
}) => {
  const [feedback, setFeedback] = useState({
    rating: 5,
    content: '',
    outcome: '',
    amountPaid: '',
    isReserved: false,
    clientSatisfaction: 5,
    notes: '',
    followUpRequired: false,
    followUpDate: '',
    sessionType: sessionData?.sessionType || 'Orientation',
    leadStatus: null,
    clientNote: '',
  });

  const [errors, setErrors] = useState({});

  const outcomeOptions = [
    { value: 'Very Interested', label: 'Very Interested', color: 'success' },
    { value: 'Interested', label: 'Interested', color: 'primary' },
    { value: 'Neutral', label: 'Neutral', color: 'default' },
    { value: 'Not Interested', label: 'Not Interested', color: 'warning' },
    { value: 'Follow Up Required', label: 'Follow Up Required', color: 'info' },
    { value: 'Reservation Made', label: 'Reservation Made', color: 'success' },
    { value: 'No Show', label: 'No Show', color: 'error' },
    { value: 'Cancelled', label: 'Cancelled', color: 'error' }
  ];

  const handleInputChange = (field, value) => {
    setFeedback(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!feedback.content.trim()) {
      newErrors.content = 'Feedback content is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    console.log("feedback");
    if (validateForm()) {
      onSubmit(feedback);
    }
  };

  const handleClose = () => {
    setFeedback({
      rating: 5,
      content: '',
      outcome: '',
      amountPaid: '',
      isReserved: false,
      clientSatisfaction: 5,
      notes: '',
      followUpRequired: false,
      followUpDate: '',
      sessionType: sessionData?.sessionType || 'Orientation',
      leadStatus: null,
      clientNote: '',
    });
    setErrors({});
    onClose();
  };

  const getOutcomeIcon = (outcome) => {
    switch (outcome) {
      case 'Very Interested':
      case 'Reservation Made':
        return <CheckCircle />;
      case 'Not Interested':
      case 'No Show':
      case 'Cancelled':
        return <Cancel />;
      case 'Follow Up Required':
        return <PersonAdd />;
      default:
        return <Star />;
    }
  };

  return (
    <StyledDialog 
      open={open} 
      onClose={handleClose}
      // maxWidth="md"
      // fullWidth
    >
      <DialogTitle sx={{ 
        background: 'linear-gradient(135deg, #465064 0%, #667eea 100%)',
        color: 'white',
        textAlign: 'center',
        py: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <Star sx={{ fontSize: '1.5rem' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Session Feedback
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
          Help us improve by sharing your experience
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Session Info */}
        <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Session Details
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Type: {feedback.sessionType}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Client: {sessionData?.clientName}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Started: {sessionData?.startTime ? new Date(sessionData.startTime).toLocaleString() : 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Duration: {sessionData?.duration || 'In Progress'}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Overall Rating */}
        <FeedbackSection>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Star color="primary" />
            Overall Session Rating
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Rating
              value={feedback.rating}
              onChange={(event, newValue) => handleInputChange('rating', newValue)}
              size="large"
              icon={<Star fontSize="inherit" />}
              emptyIcon={<Star fontSize="inherit" />}
            />
            <Typography variant="body2" color="text.secondary">
              {feedback.rating === 5 ? 'Excellent' : 
               feedback.rating === 4 ? 'Good' : 
               feedback.rating === 3 ? 'Average' : 
               feedback.rating === 2 ? 'Poor' : 'Very Poor'}
            </Typography>
          </Box>
        </FeedbackSection>

        {/* Client Satisfaction */}
        <FeedbackSection>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <ThumbUp color="primary" />
            Client Satisfaction
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Rating
              value={feedback.clientSatisfaction}
              onChange={(event, newValue) => handleInputChange('clientSatisfaction', newValue)}
              size="large"
              icon={<ThumbUp fontSize="inherit" />}
              emptyIcon={<ThumbUp fontSize="inherit" />}
            />
            <Typography variant="body2" color="text.secondary">
              {feedback.clientSatisfaction === 5 ? 'Very Satisfied' : 
               feedback.clientSatisfaction === 4 ? 'Satisfied' : 
               feedback.clientSatisfaction === 3 ? 'Neutral' : 
               feedback.clientSatisfaction === 2 ? 'Dissatisfied' : 'Very Dissatisfied'}
            </Typography>
          </Box>
        </FeedbackSection>

        {/* Outcome Selection */}
        {/* <FeedbackSection>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Session Outcome *
          </Typography>
          <FormControl fullWidth error={!!errors.outcome}>
            <InputLabel>Select Outcome</InputLabel>
            <Select
              value={feedback.outcome}
              onChange={(e) => handleInputChange('outcome', e.target.value)}
              label="Select Outcome"
            >
              {outcomeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getOutcomeIcon(option.value)}
                    {option.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
            {errors.outcome && (
              <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                {errors.outcome}
              </Typography>
            )}
          </FormControl>
        </FeedbackSection> */}

        {/* Financial Information */}
        {/* {(feedback.outcome === 'Reservation Made' || feedback.outcome === 'Very Interested') && (
          <FeedbackSection>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AttachMoney color="primary" />
              Financial Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Amount Paid"
                  type="number"
                  value={feedback.amountPaid}
                  onChange={(e) => handleInputChange('amountPaid', e.target.value)}
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={feedback.isReserved}
                      onChange={(e) => handleInputChange('isReserved', e.target.checked)}
                    />
                  }
                  label="Reservation Confirmed"
                />
              </Grid>
            </Grid>
          </FeedbackSection>
        )} */}

        {/* Feedback Content */}
        <FeedbackSection>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Detailed Feedback *
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={feedback.content}
            onChange={(e) => handleInputChange('content', e.target.value)}
            placeholder="Share your experience, client feedback, key points discussed, etc."
            error={!!errors.content}
            helperText={errors.content || "Please provide detailed feedback about the session"}
          />
        </FeedbackSection>

        {/* Follow-up Information */}
        {/* <FeedbackSection>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Follow-up Information
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={feedback.followUpRequired}
                onChange={(e) => handleInputChange('followUpRequired', e.target.checked)}
              />
            }
            label="Follow-up Required"
            sx={{ mb: 2 }}
          />
          
          {feedback.followUpRequired && (
            <TextField
              fullWidth
              type="datetime-local"
              label="Follow-up Date & Time"
              value={feedback.followUpDate}
              onChange={(e) => handleInputChange('followUpDate', e.target.value)}
              error={!!errors.followUpDate}
              helperText={errors.followUpDate}
              InputLabelProps={{
                shrink: true,
              }}
            />
          )}
        </FeedbackSection> */}

        {/* Client Lead Status */}
        {(sessionData?.sessionType === 1 || sessionData?.sessionType === 'Meeting') && (
          <FeedbackSection>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonAdd color="primary" />
              Client Lead Status
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Update Lead Status</InputLabel>
              <Select
                value={feedback.leadStatus ?? ''}
                label="Update Lead Status"
                onChange={(e) => handleInputChange('leadStatus', e.target.value === '' ? null : e.target.value)}
              >
                <MenuItem value=""><em>No change</em></MenuItem>
                <MenuItem value={0}>Interested</MenuItem>
                <MenuItem value={1}>Not Interested</MenuItem>
                <MenuItem value={2}>Call Him Back</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              multiline
              rows={2}
              value={feedback.clientNote}
              onChange={(e) => handleInputChange('clientNote', e.target.value)}
              placeholder="Add a note to the client's profile..."
              label="Client Note"
              size="small"
              sx={{ mt: 2 }}
            />
          </FeedbackSection>
        )}

        {/* Additional Notes */}
        <FeedbackSection>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Session Notes
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={feedback.notes}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            placeholder="Any additional comments, suggestions, or important details..."
          />
        </FeedbackSection>

        {/* Summary */}
        {feedback.outcome && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Summary:</strong> {feedback.outcome} - 
              {feedback.isReserved ? ' Reservation confirmed' : ''}
              {feedback.amountPaid ? ` - Amount: $${feedback.amountPaid}` : ''}
              {feedback.followUpRequired ? ' - Follow-up scheduled' : ''}
            </Typography>
          </Alert>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 3, gap: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={loading}
          sx={{ minWidth: 100 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
          sx={{ 
            minWidth: 120,
            background: 'linear-gradient(135deg, #465064 0%, #667eea 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #3a4254 0%, #5a6fd8 100%)',
            }
          }}
        >
          {loading ? 'Submitting...' : 'Submit Feedback'}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default SessionFeedbackModal;
